import { STORAGE_KEYS, WORKFLOW_QUERY } from '@/utils/constants';
import { conversationsBase } from '@/config/routes';
import type { UserRole } from '@/types';

export interface DeviceSetupConversationMeta {
  id: string;
  title: string;
  createdAt: string;
}

function readRegistry(): DeviceSetupConversationMeta[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.DEVICE_SETUP_CONVERSATIONS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DeviceSetupConversationMeta[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRegistry(entries: DeviceSetupConversationMeta[]) {
  localStorage.setItem(STORAGE_KEYS.DEVICE_SETUP_CONVERSATIONS, JSON.stringify(entries));
}

export function registerDeviceSetupConversation(
  conversationId: string,
  title = 'New controller onboarding'
) {
  const entries = readRegistry();
  if (entries.some((e) => e.id === conversationId)) return;
  writeRegistry([
    { id: conversationId, title, createdAt: new Date().toISOString() },
    ...entries,
  ]);
}

export function isDeviceSetupConversation(conversationId: string): boolean {
  return readRegistry().some((e) => e.id === conversationId);
}

export function getDeviceSetupConversations(): DeviceSetupConversationMeta[] {
  return readRegistry();
}

export function buildDeviceSetupNavigateUrl(
  conversationId: string,
  draftMessage?: string,
  role: UserRole = 'operator'
): string {
  const base = `${conversationsBase(role)}/${conversationId}?workflow=${WORKFLOW_QUERY.DEVICE_SETUP}`;
  if (!draftMessage?.trim()) return base;
  return `${base}&draft=${encodeURIComponent(draftMessage.trim())}`;
}

/** Local template flows work without the conversations API. */
export function createLocalDeviceSetupConversationId(): string {
  return `device-setup-${crypto.randomUUID()}`;
}

export function isLocalDeviceSetupConversation(conversationId: string): boolean {
  return conversationId.startsWith('device-setup-');
}

// --- User Creation ---

const USER_CREATION_CONVERSATIONS_KEY = 'ux3_user_creation_conversations';

interface UserCreationConversationMeta {
  id: string;
  title: string;
  createdAt: string;
}

function readUserCreationRegistry(): UserCreationConversationMeta[] {
  try {
    const raw = localStorage.getItem(USER_CREATION_CONVERSATIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as UserCreationConversationMeta[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeUserCreationRegistry(entries: UserCreationConversationMeta[]) {
  localStorage.setItem(USER_CREATION_CONVERSATIONS_KEY, JSON.stringify(entries));
}

export function registerUserCreationConversation(
  conversationId: string,
  title = 'New user onboarding'
) {
  const entries = readUserCreationRegistry();
  if (entries.some((e) => e.id === conversationId)) return;
  writeUserCreationRegistry([
    { id: conversationId, title, createdAt: new Date().toISOString() },
    ...entries,
  ]);
}

export function isUserCreationConversation(conversationId: string): boolean {
  return readUserCreationRegistry().some((e) => e.id === conversationId) ||
    conversationId.startsWith('user-creation-');
}

export function createLocalUserCreationConversationId(): string {
  return `user-creation-${crypto.randomUUID()}`;
}

export function buildUserCreationNavigateUrl(
  conversationId: string,
  draftMessage?: string,
  role: UserRole = 'novice'
): string {
  const base = `${conversationsBase(role)}/${conversationId}?workflow=${WORKFLOW_QUERY.USER_CREATION}`;
  if (!draftMessage?.trim()) return base;
  return `${base}&draft=${encodeURIComponent(draftMessage.trim())}`;
}

// --- Access group creation ---

interface AccessGroupCreationConversationMeta {
  id: string;
  title: string;
  createdAt: string;
}

function readAccessGroupCreationRegistry(): AccessGroupCreationConversationMeta[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.ACCESS_GROUP_CREATION_CONVERSATIONS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AccessGroupCreationConversationMeta[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAccessGroupCreationRegistry(entries: AccessGroupCreationConversationMeta[]) {
  localStorage.setItem(STORAGE_KEYS.ACCESS_GROUP_CREATION_CONVERSATIONS, JSON.stringify(entries));
}

export function registerAccessGroupCreationConversation(
  conversationId: string,
  title = 'New access group onboarding'
) {
  const entries = readAccessGroupCreationRegistry();
  if (entries.some((e) => e.id === conversationId)) return;
  writeAccessGroupCreationRegistry([
    { id: conversationId, title, createdAt: new Date().toISOString() },
    ...entries,
  ]);
}

export function isAccessGroupCreationConversation(conversationId: string): boolean {
  return (
    readAccessGroupCreationRegistry().some((e) => e.id === conversationId) ||
    conversationId.startsWith('access-group-creation-')
  );
}

export function createLocalAccessGroupCreationConversationId(): string {
  return `access-group-creation-${crypto.randomUUID()}`;
}

export function buildAccessGroupCreationNavigateUrl(
  conversationId: string,
  draftMessage?: string,
  role: UserRole = 'novice'
): string {
  const base = `${conversationsBase(role)}/${conversationId}?workflow=${WORKFLOW_QUERY.ACCESS_GROUP_CREATION}`;
  if (!draftMessage?.trim()) return base;
  return `${base}&draft=${encodeURIComponent(draftMessage.trim())}`;
}

// --- Bulk user import ---

interface BulkUserImportConversationMeta {
  id: string;
  title: string;
  createdAt: string;
}

function readBulkUserImportRegistry(): BulkUserImportConversationMeta[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.BULK_USER_IMPORT_CONVERSATIONS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as BulkUserImportConversationMeta[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeBulkUserImportRegistry(entries: BulkUserImportConversationMeta[]) {
  localStorage.setItem(STORAGE_KEYS.BULK_USER_IMPORT_CONVERSATIONS, JSON.stringify(entries));
}

export function registerBulkUserImportConversation(
  conversationId: string,
  title = 'Bulk user import'
) {
  const entries = readBulkUserImportRegistry();
  if (entries.some((e) => e.id === conversationId)) return;
  writeBulkUserImportRegistry([
    { id: conversationId, title, createdAt: new Date().toISOString() },
    ...entries,
  ]);
}

export function isBulkUserImportConversation(conversationId: string): boolean {
  return (
    readBulkUserImportRegistry().some((e) => e.id === conversationId) ||
    conversationId.startsWith('bulk-user-import-')
  );
}

export function createLocalBulkUserImportConversationId(): string {
  return `bulk-user-import-${crypto.randomUUID()}`;
}

export function buildBulkUserImportNavigateUrl(
  conversationId: string,
  draftMessage?: string,
  role: UserRole = 'novice'
): string {
  const base = `${conversationsBase(role)}/${conversationId}?workflow=${WORKFLOW_QUERY.BULK_USER_IMPORT}`;
  if (!draftMessage?.trim()) return base;
  return `${base}&draft=${encodeURIComponent(draftMessage.trim())}`;
}
